'use client';

import { useState, useEffect } from 'react';
import BibleReaderTopBar from './BibleReaderTopBar';
import BibleReaderMain from './BibleReaderMain';
import BookOutlinePanel from './BookOutlinePanel';
import VerseDrawer from './VerseDrawer';
import CrossReferencePopup from './CrossReferencePopup';
import { supabaseBibleRepository } from '@/lib/repositories/supabaseBibleRepository';

export default function BibleReaderClient() {
  // Add mounted state to prevent hydration mismatch
  const [isMounted, setIsMounted] = useState(false);
  
  // Core state
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [selectedVersion, setSelectedVersion] = useState('KJV_EN');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [viewMode, setViewMode] = useState('single');
  
  // New state for available books
  const [availableBooks, setAvailableBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(true);
  
  // UI state
  const [showBookOutline, setShowBookOutline] = useState(true);
  const [showCrossRefs, setShowCrossRefs] = useState(true);
  const [showCommentary, setShowCommentary] = useState(true);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [crossRefPopup, setCrossRefPopup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Highlight state
  const [highlights, setHighlights] = useState({});
  const [selectedHighlightColor, setSelectedHighlightColor] = useState('yellow');
  const [showHighlightPanel, setShowHighlightPanel] = useState(false);
  
  // Data
  const [verses, setVerses] = useState([]);
  const [bookInfo, setBookInfo] = useState(null);
  const [bookIntro, setBookIntro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Set mounted flag after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // NEW: Load available books from database on mount
  useEffect(() => {
    async function loadAvailableBooks() {
      if (!isMounted) return;
      
      try {
        setBooksLoading(true);
        setError(null); // Clear any previous errors
        
        // Validate translation code exists before loading books
        const translations = await supabaseBibleRepository?.listTranslations();
        const translationExists = translations?.some(t => t?.code === selectedVersion);
        
        if (!translationExists) {
          // Fallback to first available translation or KJV_EN
          const fallbackCode = translations?.[0]?.code || 'KJV_EN';
          console.warn(`Translation "${selectedVersion}" not found. Falling back to "${fallbackCode}"`);
          setSelectedVersion(fallbackCode);
          setError(`Translation "${selectedVersion}" is not available. Using "${fallbackCode}" instead.`);
          return;
        }
        
        const books = await supabaseBibleRepository?.listBooks(selectedVersion);
        setAvailableBooks(books);
        
        // Set default book to first available book if not already set
        if (!selectedBook && books?.length > 0) {
          setSelectedBook(books?.[0]?.bookCode);
        }
      } catch (error) {
        console.error('Failed to load available books:', error);
        setError(`Failed to load Bible data: ${error?.message || 'Unknown error'}`);
        setAvailableBooks([]);
      } finally {
        setBooksLoading(false);
      }
    }

    loadAvailableBooks();
  }, [isMounted, selectedVersion]);

  // Load Bible data from Supabase when book/chapter/version changes
  useEffect(() => {
    async function fetchBibleData() {
      // Don't fetch if no book is selected or books are still loading
      if (!selectedBook || booksLoading) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Use selectedBook directly - it's already the database book code
        const translationCode = selectedVersion;
        
        // Validate translation exists before fetching book
        const translationData = await supabaseBibleRepository?.getTranslationByCode(translationCode);
        if (!translationData) {
          setError(`Translation "${translationCode}" is not available in the database. Please select a different translation.`);
          setBookInfo(null);
          setBookIntro(null);
          setVerses([]);
          setLoading(false);
          return;
        }
        
        // Fetch book information
        const book = await supabaseBibleRepository?.getBookByCode(translationCode, selectedBook);
        
        if (!book) {
          setError(`Book "${selectedBook}" not found in ${translationCode} translation`);
          setBookInfo(null);
          setBookIntro(null);
          setVerses([]);
          setLoading(false);
          return;
        }

        setBookInfo({
          id: book?.id,
          name: book?.bookName,
          shortName: book?.shortName,
          bookCode: book?.bookCode,
          chapterCount: book?.chapterCount,
        });

        // Fetch book metadata (outline and introduction)
        const bookMeta = await supabaseBibleRepository?.getBookMeta(book?.id);
        
        if (bookMeta && (bookMeta?.outline || bookMeta?.introduction)) {
          // Parse outline if it's stored as JSON string
          let parsedOutline = null;
          if (bookMeta?.outline) {
            try {
              parsedOutline = typeof bookMeta?.outline === 'string' 
                ? JSON.parse(bookMeta?.outline) 
                : bookMeta?.outline;
            } catch (e) {
              console.error('Failed to parse outline:', e);
            }
          }

          // Parse introduction if it's stored as JSON string  
          let parsedIntroduction = bookMeta?.introduction;
          if (bookMeta?.introduction) {
            try {
              parsedIntroduction = typeof bookMeta?.introduction === 'string'
                ? JSON.parse(bookMeta?.introduction)
                : bookMeta?.introduction;
            } catch (e) {
              // If not JSON, use as plain string
              parsedIntroduction = bookMeta?.introduction;
            }
          }

          setBookIntro(parsedIntroduction);
        } else {
          setBookIntro(null);
        }

        // Fetch chapter data
        const chapter = await supabaseBibleRepository?.getChapter(book?.id, selectedChapter);
        
        if (!chapter) {
          setError(`Chapter ${selectedChapter} not found`);
          setVerses([]);
          setLoading(false);
          return;
        }

        // Fetch verses for the chapter
        const versesList = await supabaseBibleRepository?.listVerses(chapter?.id);
        
        // Transform verses to expected format
        const transformedVerses = versesList?.map(verse => ({
          verse: verse?.verseNumber,
          text: verse?.text,
          id: verse?.id,
          chapterId: verse?.chapterId,
        }));

        setVerses(transformedVerses);
        
      } catch (err) {
        console.error('Error fetching Bible data:', err);
        // Provide more specific error message
        const errorMsg = err?.message?.includes('invalid input value for enum') 
          ? `Translation code "${selectedVersion}" is not valid. Please select a different translation.`
          : err?.message || 'Failed to load Bible data';
        setError(errorMsg);
        setVerses([]);
        setBookInfo(null);
        setBookIntro(null);
      } finally {
        setLoading(false);
      }
    }

    fetchBibleData();
  }, [selectedBook, selectedChapter, selectedVersion, selectedLanguage, booksLoading]);

  // Save layout preferences only after mounted
  useEffect(() => {
    if (!isMounted) return;
    
    const preferences = {
      showBookOutline,
      showCrossRefs,
      showCommentary,
      viewMode
    };
    localStorage.setItem('bibleReaderLayout', JSON.stringify(preferences));
  }, [isMounted, showBookOutline, showCrossRefs, showCommentary, viewMode]);

  const handleBookChange = (bookCode) => {
    setSelectedBook(bookCode);
    setSelectedChapter(1);
    setSelectedVerse(null);
  };

  const handleChapterChange = (chapter) => {
    setSelectedChapter(chapter);
    setSelectedVerse(null);
  };

  const handleVerseClick = (verseNumber) => {
    setSelectedVerse(verseNumber);
  };

  const handleHighlightVerse = (verseKey, color) => {
    const newHighlights = { ...highlights };
    if (newHighlights?.[verseKey] === color) {
      // Remove highlight if clicking same color
      delete newHighlights?.[verseKey];
    } else {
      newHighlights[verseKey] = color;
    }
    setHighlights(newHighlights);
    localStorage.setItem('bibleHighlights', JSON.stringify(newHighlights));
  };

  const handleClearHighlights = () => {
    if (confirm('Are you sure you want to clear all highlights?')) {
      setHighlights({});
      localStorage.removeItem('bibleHighlights');
    }
  };

  const handleCrossRefClick = (reference, position) => {
    setCrossRefPopup({ reference, position });
  };

  const toggleLayout = (panelName) => {
    switch(panelName) {
      case 'outline':
        setShowBookOutline(!showBookOutline);
        break;
      case 'crossrefs':
        setShowCrossRefs(!showCrossRefs);
        break;
      case 'commentary':
        setShowCommentary(!showCommentary);
        break;
      default:
        break;
    }
  };

  // Don't render until mounted and books are loaded to prevent hydration mismatch
  if (!isMounted || booksLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          {!isMounted ? 'Loading Bible Reader...' : 'Loading available books...'}
        </div>
      </div>
    );
  }

  // Show error if no books available
  if (availableBooks?.length === 0 && !booksLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <div className="text-destructive text-lg font-semibold mb-2">
            No Bible Books Available
          </div>
          <p className="text-muted-foreground">
            Please import Bible data into the database to use the Bible Reader.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg mb-4 mx-4 mt-4">
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}
      
      {/* Top Navigation Bar - Sticky */}
      <div className="sticky top-0 z-40 bg-card border-b border-border shadow-sm">
        <BibleReaderTopBar
          selectedBook={selectedBook}
          selectedChapter={selectedChapter}
          selectedVersion={selectedVersion}
          selectedLanguage={selectedLanguage}
          viewMode={viewMode}
          searchQuery={searchQuery}
          availableBooks={availableBooks}
          onBookChange={handleBookChange}
          onChapterChange={handleChapterChange}
          onVersionChange={setSelectedVersion}
          onLanguageChange={setSelectedLanguage}
          onViewModeChange={setViewMode}
          onSearchChange={setSearchQuery}
          bookInfo={bookInfo}
        />
      </div>
      {/* Main Content Area */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Book Outline Sidebar - Left */}
          {showBookOutline && (
            <div className="col-span-12 lg:col-span-3">
              <BookOutlinePanel
                bookIntro={bookIntro}
                currentChapter={selectedChapter}
                onChapterSelect={handleChapterChange}
                onClose={() => setShowBookOutline(false)}
              />
            </div>
          )}

          {/* Main Reading Area - Center */}
          <div className={`col-span-12 ${showBookOutline ? 'lg:col-span-9' : 'lg:col-span-12'}`}>
            <BibleReaderMain
              verses={verses}
              bookInfo={bookInfo}
              selectedChapter={selectedChapter}
              selectedVersion={selectedVersion}
              selectedLanguage={selectedLanguage}
              viewMode={viewMode}
              showCrossRefs={showCrossRefs}
              showCommentary={showCommentary}
              loading={loading}
              searchQuery={searchQuery}
              highlights={highlights}
              selectedHighlightColor={selectedHighlightColor}
              onVerseClick={handleVerseClick}
              onCrossRefClick={handleCrossRefClick}
              onToggleLayout={toggleLayout}
              onHighlightVerse={handleHighlightVerse}
              onChapterChange={handleChapterChange}
            />
          </div>
        </div>
      </div>
      {/* Verse Drawer - Right Side */}
      {selectedVerse && (
        <VerseDrawer
          bookId={selectedBook}
          chapter={selectedChapter}
          verse={selectedVerse}
          onClose={() => setSelectedVerse(null)}
        />
      )}
      {/* Cross-Reference Popup */}
      {crossRefPopup && (
        <CrossReferencePopup
          reference={crossRefPopup?.reference}
          position={crossRefPopup?.position}
          onClose={() => setCrossRefPopup(null)}
        />
      )}
      {/* Floating Layout Controls */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-30">
        <button
          onClick={() => setShowHighlightPanel(!showHighlightPanel)}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
            showHighlightPanel 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card border border-border hover:bg-accent'
          }`}
          title="Highlight Tools"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
        </button>
        
        <button
          onClick={() => toggleLayout('outline')}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
            showBookOutline 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card border border-border hover:bg-accent'
          }`}
          title="Toggle Book Outline"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        
        <button
          onClick={() => toggleLayout('crossrefs')}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
            showCrossRefs 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card border border-border hover:bg-accent'
          }`}
          title="Toggle Cross-References"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
      </div>
      {/* Highlight Panel */}
      {showHighlightPanel && (
        <div className="fixed bottom-24 right-6 bg-card border border-border rounded-lg shadow-xl p-4 z-30 w-64">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              Highlight Colors
            </h3>
            <button
              onClick={() => setShowHighlightPanel(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Select a color, then click verses to highlight
            </p>
            
            {/* Color Palette */}
            <div className="grid grid-cols-4 gap-2">
              {[
                { name: 'yellow', color: '#fef08a', label: 'Yellow' },
                { name: 'green', color: '#86efac', label: 'Green' },
                { name: 'blue', color: '#93c5fd', label: 'Blue' },
                { name: 'pink', color: '#f9a8d4', label: 'Pink' },
                { name: 'purple', color: '#d8b4fe', label: 'Purple' },
                { name: 'orange', color: '#fdba74', label: 'Orange' },
                { name: 'red', color: '#fca5a5', label: 'Red' },
                { name: 'teal', color: '#5eead4', label: 'Teal' }
              ]?.map(({ name, color, label }) => (
                <button
                  key={name}
                  onClick={() => setSelectedHighlightColor(name)}
                  className={`h-10 rounded-lg transition-all border-2 ${
                    selectedHighlightColor === name 
                      ? 'border-foreground scale-110 shadow-lg' 
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                  title={label}
                />
              ))}
            </div>
            
            {/* Current Selection */}
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-1">Selected:</p>
              <div className="flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded border border-border"
                  style={{ 
                    backgroundColor: {
                      yellow: '#fef08a', green: '#86efac', blue: '#93c5fd',
                      pink: '#f9a8d4', purple: '#d8b4fe', orange: '#fdba74',
                      red: '#fca5a5', teal: '#5eead4'
                    }?.[selectedHighlightColor] 
                  }}
                />
                <span className="text-sm font-medium capitalize">{selectedHighlightColor}</span>
              </div>
            </div>
            
            {/* Stats */}
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-1">Highlights:</p>
              <p className="text-sm font-medium">{Object.keys(highlights)?.length} verses</p>
            </div>
            
            {/* Clear All */}
            {Object.keys(highlights)?.length > 0 && (
              <button
                onClick={handleClearHighlights}
                className="w-full px-3 py-2 text-sm bg-destructive/10 text-destructive rounded-lg hover:bg-destructive/20 transition-colors"
              >
                Clear All Highlights
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}